/**
 * 처방 검수 상세 화면.
 * admin-ruda-web `prescriptionReviewDetails.js` 포팅.
 */
import {
  prescriptionKeys,
  useAutoMatchingRetry,
  useCancelPrescriptionFinalization,
  useCancelPrescriptionImageExclusion,
  useCreateDrugPrescription,
  useCreateDrugPrescriptions,
  useDeletePrescriptionDrugsByImageCode,
  useDeletePrescriptionDrug,
  useDeletePrescriptionImage,
  useExcludePrescriptionImage,
  useFinalizePrescription,
  useFindDuplicateFinalizedDrugs,
  usePrescriptionDrugs,
  usePrescriptionImageDetail,
  useUpdatePrescriptionDrug,
  useUpdatePrescriptionImage,
} from '@/entities/prescription/hooks/usePrescriptions';
import { usePrescriptionAutoOcrPolling } from '@/entities/prescription/hooks/usePrescriptionAutoOcrPolling';
import { ocrService } from '@/entities/ocr/api/ocr.api';
import { preprocessService } from '@/entities/ocr/api/preprocess.api';
import { useDrugSearchByCode } from '@/entities/drug/hooks/useDrugs';
import {
  normalizeDrugCodeSearchResponse,
  type DrugCodeSearchResponse,
} from '@/entities/drug/api/drug.api';
import {
  buildUpdatePrescriptionDrugRequest,
  toNullablePharmaCompanyId,
  type DuplicateFinalizedPrescriptionDrug,
  type PrescriptionDrugInfoResponse,
  type PrescriptionImageExclusionReason,
} from '@/entities/prescription/api/prescription.api';
import { EditedOcrComparisonModal } from '@/pages/prescriptions/EditedOcrComparisonModal';
import { PrescriptionDrugNameSearchDialog } from '@/pages/prescriptions/PrescriptionDrugNameSearchDialog';
import { CrossImageDuplicateModal } from '@/pages/prescriptions/CrossImageDuplicateModal';
import type { PreviousDrugAddition } from '@/pages/prescriptions/PreviousPrescriptionDrugsPanel';
import { usePreviousDrugsWindowHost } from '@/pages/prescriptions/previousDrugsWindowChannel';
import { WithinImageDuplicateModal } from '@/pages/prescriptions/WithinImageDuplicateModal';
import {
  applyDrugMatchingResults,
  getPrescriptionRowKey,
  isStagingDrugRow,
  mapEditedOcrRowsToStagingDrugs,
  mergeFetchedDrugsWithFallbackByDrugCode,
  withPrescriptionTempRowKey,
  type StagingPrescriptionDrug,
} from '@/pages/prescriptions/prescriptionStagingUtils';
import { buildDrugNameSearchKeywordWhenNotEditing } from '@/pages/prescriptions/drugSearchKeywords';
import {
  buildCrossImageDuplicateDrugs,
  findDuplicatedDrugCodesInList,
} from '@/pages/prescriptions/prescriptionDuplicateUtils';
import {
  AUTO_OCR_RETRYABLE_STATUSES,
  getAutoOcrStatusMeta,
  mapXlsxRowsToCreateRequests,
  normalizeEditedOcrItems,
  type EditedOcrDrugRow,
} from '@/pages/prescriptions/prescriptionOcrUtils';
import { QuantityInput } from '@/pages/prescriptions/QuantityInput';
import { PRESCRIPTION_XLSX_HEADER_MAP } from '@/pages/prescriptions/prescriptionXlsx';
import {
  formatPrice,
  getAdminCheckStatusLabel,
  getExclusionReasonLabel,
} from '@/pages/prescriptions/prescriptionUtils';
import { PrescriptionExclusionDialog } from '@/pages/prescriptions/PrescriptionExclusionDialog';
import { formatDateTimeMinute } from '@/shared/lib/dateFormat';
import { PrescriptionHighlighterDialog } from '@/pages/prescriptions/PrescriptionHighlighterDialog';
import {
  MenuAccessContext,
  useMenuAccess,
} from '@/layouts/MenuAccessContext';
import { useConfirmDialog } from '@/shared/components/ConfirmDialogProvider';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { hasFullAccessFromRoles } from '@/shared/utils/authRoles';
import { useAuthStore } from '@/stores/authStore';
import {
  OcrCropWarpUploader,
  type OcrCropWarpFileItem,
  type OcrCropWarpSelection,
} from '@/shared/components/OcrCropWarpUploader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { exportXlsxTemplate, importFromXlsx } from '@/shared/utils/xlsxExport';
import {
  getBoundingBoxFromPoints,
  getInitialOcrCornerPoints,
} from '@/shared/utils/ocrCropWarpUtils';
import type { Point2D } from '@/shared/utils/ocrImagePreprocess';
import { DownloadWithPreview } from '@/shared/components/DownloadWithPreview';
import { FileUploadButton } from '@/shared/components/FileUploadButton';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { PageHeader } from '@/shared/components/PageHeader';
import { DatePicker, MonthPicker } from '@/shared/components/DatePicker';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import {
  prescriptionReviewDetailPath,
  ROUTE_PATHS,
} from '@/shared/constants/routes';
import {
  buildOcrCoordinateStorageKey,
  loadOcrCoordinatesFromSession,
  OCR_COORDINATE_STORAGE_PREFIX,
  saveOcrCoordinatesToSession,
} from '@/shared/lib/ocrCoordinateStorage';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useUIStore } from '@/stores/uiStore';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useRouter, useSearch, useLocation } from '@tanstack/react-router';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  FileText,
  Highlighter,
  History,
  Plus,
  ScanLine,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ToolbarRow } from '@/shared/components/ui/row';
import { DataCard } from '@/shared/components/DataCard';

/** 원외·원내 수량 — 셀 너비를 채우고, X는 인풋 테두리 안쪽 */
const QUANTITY_INPUT_CLASS_NAME =
  'h-9 w-full min-w-16 px-2 text-center tabular-nums';

/** `data ?? []`는 렌더마다 새 배열이 되어 선택 effect가 무한 setState 한다 */
const EMPTY_PRESCRIPTION_DRUGS: PrescriptionDrugInfoResponse[] = [];

/**
 * 사라진 행의 선택을 걷어내되, 결과가 같으면 같은 배열을 그대로 둔다.
 * filter는 항상 새 배열을 만들어 의존값이 매 렌더 바뀌면 최대 업데이트 깊이를 넘긴다.
 */
export const keepSelectedRowKeys = (
  selected: string[],
  validKeys: readonly string[],
): string[] => {
  const valid = new Set(validKeys);
  const next = selected.filter((key) => valid.has(key));
  if (
    next.length === selected.length &&
    next.every((key, index) => key === selected[index])
  ) {
    return selected;
  }
  return next;
};

const isSearchEnterKey = (event: React.KeyboardEvent) =>
  event.key === 'Enter' && !event.nativeEvent.isComposing;

export const PrescriptionReviewDetailPage: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirmDialog();
  const menuAccess = useMenuAccess();
  const { canWrite, readOnly } = menuAccess;
  const authUser = useAuthStore((s) => s.user);
  const setGlobalLoading = useUIStore((s) => s.setGlobalLoading);
  const navigate = useNavigate();
  const router = useRouter();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { prescriptionImageCode: codeParam } = useParams({
    strict: false,
  }) as { prescriptionImageCode?: string };
  const search = useSearch({ strict: false }) as { refYm?: string };

  /** 목록 → 상세 진입 시 넘긴 조회조건 (삭제·나가기 복원용) */
  const listReturnSearch = (
    location.state as
      | { prescriptionReviewListSearch?: Record<string, string | number | boolean> }
      | undefined
  )?.prescriptionReviewListSearch;

  const prescriptionImageCode = codeParam
    ? decodeURIComponent(codeParam)
    : '';
  const routeRefYm = search.refYm ?? '';

  const { data: imageDetail, isLoading: imageLoading } =
    usePrescriptionImageDetail(prescriptionImageCode || null);

  // 레거시 getEffectiveRefYm: 쿼리 refYm 없으면 상세의 처방년월 폴백
  const effectiveRefYm =
    routeRefYm || imageDetail?.prescriptionYearMonth || '';

  const { data: drugList, isLoading: drugsLoading, refetch: refetchDrugs } =
    usePrescriptionDrugs(
      prescriptionImageCode || null,
      effectiveRefYm || null,
    );
  const drugs = drugList ?? EMPTY_PRESCRIPTION_DRUGS;

  const updateImage = useUpdatePrescriptionImage();
  const deletePrescription = useDeletePrescriptionImage();
  const finalize = useFinalizePrescription();
  const cancelFinalization = useCancelPrescriptionFinalization();
  const excludeImage = useExcludePrescriptionImage();
  const cancelExclusion = useCancelPrescriptionImageExclusion();
  const findDuplicates = useFindDuplicateFinalizedDrugs();
  const createDrugs = useCreateDrugPrescriptions();
  const createDrug = useCreateDrugPrescription();
  const deleteDrugs = useDeletePrescriptionDrugsByImageCode();
  const autoMatchingRetry = useAutoMatchingRetry();
  const deleteDrug = useDeletePrescriptionDrug();
  const updateDrug = useUpdatePrescriptionDrug();
  const searchDrugByCode = useDrugSearchByCode();

  const [stagingRows, setStagingRows] = useState<StagingPrescriptionDrug[]>([]);
  const [drugMatching, setDrugMatching] = useState(false);
  const [ocrRequesting, setOcrRequesting] = useState(false);
  const [nameSearchOpen, setNameSearchOpen] = useState(false);
  const [nameSearchTargetKey, setNameSearchTargetKey] = useState<string | null>(
    null,
  );
  /** 이름 검색 결과를 서버에 즉시 PATCH할지 (저장행 persist) */
  const [nameSearchPersist, setNameSearchPersist] = useState(false);
  const [nameSearchDraftName, setNameSearchDraftName] = useState('');
  const [nameSearchKeyword, setNameSearchKeyword] = useState('');
  /** 레거시 editingRowKey — 저장행 인라인 편집 (한 행만) */
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<{
    drugCode: string;
    drugName: string;
    price: number;
    outHospitalQuantity: number;
    inHospitalQuantity: number;
    pharmaCompanyName: string;
    pharmaCompanyId: number;
    isDrugInfoExists: boolean | null;
  } | null>(null);
  const [rowSaving, setRowSaving] = useState(false);
  const [xlsxUploading, setXlsxUploading] = useState(false);
  const [xlsxUploadDialogOpen, setXlsxUploadDialogOpen] = useState(false);
  const xlsxInputRef = useRef<HTMLInputElement>(null);
  const drugCodeInputRef = useRef<HTMLInputElement>(null);
  const [ocrEditorOpen, setOcrEditorOpen] = useState(false);
  const [ocrEditorPreparing, setOcrEditorPreparing] = useState(false);
  const [editedOcrImages, setEditedOcrImages] = useState<OcrCropWarpFileItem[]>([]);
  /** 편집기에서 적용 없이 조정 중인 선택 영역 */
  const [ocrLiveSelection, setOcrLiveSelection] =
    useState<OcrCropWarpSelection | null>(null);
  const [ocrSourceCorners, setOcrSourceCorners] = useState<Point2D[] | null>(null);
  const [editedOcrComparing, setEditedOcrComparing] = useState(false);
  /** 레거시 editedOcrRequestPreviewUrl — 비교 모달용 */
  const [editedOcrRequestPreviewUrl, setEditedOcrRequestPreviewUrl] = useState<
    string | null
  >(null);
  const [ocrSourceBbox, setOcrSourceBbox] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [comparisonResults, setComparisonResults] = useState<{
    default: EditedOcrDrugRow[];
    gemini: EditedOcrDrugRow[];
  }>({ default: [], gemini: [] });
  const [comparisonApplying, setComparisonApplying] = useState(false);

  const [registrationDate, setRegistrationDate] = useState('');
  const [prescriptionYearMonth, setPrescriptionYearMonth] = useState('');
  /** 앱 노출 여부 — 수정 시 PATCH에 함께 전송 */
  const [exposed, setExposed] = useState(true);
  /** 레거시 isFormChanged — 행 삭제·XLSX·다이얼로그 수정 등 */
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [withinDuplicateOpen, setWithinDuplicateOpen] = useState(false);
  const [crossDuplicateOpen, setCrossDuplicateOpen] = useState(false);
  const [crossDuplicateItems, setCrossDuplicateItems] = useState<
    DuplicateFinalizedPrescriptionDrug[]
  >([]);
  const [highlighterOpen, setHighlighterOpen] = useState(false);
  const [quitDialogOpen, setQuitDialogOpen] = useState(false);
  const [exclusionDialogOpen, setExclusionDialogOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const duplicatedDrugCodes = useMemo(
    () => findDuplicatedDrugCodesInList([...drugs, ...stagingRows]),
    [drugs, stagingRows],
  );

  const displayRows = useMemo(
    () => [...drugs, ...stagingRows],
    [drugs, stagingRows],
  );

  const hasUnsavedOcrDrugs = stagingRows.length > 0;

  // 이전 처방 불러오기에서 미저장 행과 같은 약을 다시 고르지 못하게 한다
  const stagedDrugCodes = useMemo(
    () =>
      new Set(stagingRows.map((row) => row.drugCode.trim()).filter(Boolean)),
    [stagingRows],
  );
  // 새 창 조회 이후 저장된 약도 새 창에서 '이미 등록됨'으로 막도록 함께 알린다
  const registeredDrugCodes = useMemo(
    () => new Set(drugs.map((drug) => drug.drugCode.trim()).filter(Boolean)),
    [drugs],
  );

  const isMetaDirty = Boolean(
    imageDetail &&
      (registrationDate !== (imageDetail.registrationDate?.slice(0, 10) ?? '') ||
        prescriptionYearMonth !== (imageDetail.prescriptionYearMonth ?? '') ||
        exposed !== (imageDetail.exposed ?? true)),
  );
  // 레거시: 메타 변경·미저장 staging·행 삭제/XLSX 등 isFormChanged
  const isDirty = isMetaDirty || hasUnsavedOcrDrugs || isFormChanged;

  const adminCheckStatus = imageDetail?.adminCheckStatus;
  const isVerified = adminCheckStatus === 'VERIFIED';
  const isUnverified = adminCheckStatus === 'UNVERIFIED';
  // 확정 여부는 adminCheckStatus가 아니라 finalized 필드로 판정
  const isFinalized = imageDetail?.finalized === true;
  const isSettled = imageDetail?.settled === true;
  /**
   * 처방 확정 제외 — 사유 컬럼이 곧 플래그.
   * 제외 중에는 서버가 처방약 편집·이미지 수정·단건 확정을 409(PRESCRIPTION_IMAGE_EXCLUDED)로
   * 막으므로(처방약 내역 보존) UI에서도 편집을 잠근다. 제외 지정은 확정·정산 전에만 가능.
   */
  const isExcluded = imageDetail?.exclusionReason != null;
  const canExclude = !isFinalized && !isSettled;
  /** 제외 중 편집 영역의 MenuWriteGate 를 숨기기 위한 읽기 전용 접근 권한 */
  const editAccess = isExcluded
    ? { ...menuAccess, canWrite: false, readOnly: true }
    : menuAccess;
  // 노출여부 드롭다운: 미확정일 때만 변경 가능 (제외 중엔 이미지 PATCH 차단)
  const canEditExposed = !isFinalized && !isExcluded;
  // 처방 삭제: full-access(admin) + WRITE, 미확정·미정산일 때만 활성
  const canShowDeletePrescription =
    canWrite &&
    hasFullAccessFromRoles(authUser?.roles, authUser?.role);
  const canDeletePrescription =
    canShowDeletePrescription && !isFinalized && !isSettled;
  // 레거시: UNVERIFIED|VERIFIED|dirty|행없음 → 비활성 (REJECTED·MODIFIED 확정 가능)
  // 미노출 처방은 서버에서 확정 차단(PRESCRIPTION_IMAGE_HIDDEN) → UI에서도 비활성
  // 확정 제외 처방도 동일하게 차단(PRESCRIPTION_IMAGE_EXCLUDED)
  const canFinalize =
    !isUnverified &&
    !isVerified &&
    !isDirty &&
    drugs.length > 0 &&
    imageDetail?.exposed !== false &&
    !isExcluded;
  // 레거시: dirty이거나 UNVERIFIED+행 있을 때만 수정 버튼 표시
  const showSaveButton =
    !isExcluded && (isDirty || (isUnverified && drugs.length > 0));

  const matchingYearMonth = (
    prescriptionYearMonth ||
    effectiveRefYm ||
    ''
  )
    .replace(/\D/g, '')
    .slice(0, 6);

  const markFormChanged = () => setIsFormChanged(true);

  const tableTotals = useMemo(() => {
    const totalOut = displayRows.reduce(
      (acc, row) => acc + (row.outHospitalQuantity ?? 0),
      0,
    );
    const totalIn = displayRows.reduce(
      (acc, row) => acc + (row.inHospitalQuantity ?? 0),
      0,
    );
    const totalAmount = displayRows.reduce(
      (acc, row) =>
        acc +
        ((row.outHospitalQuantity ?? 0) + (row.inHospitalQuantity ?? 0)) *
          (row.price ?? 0),
      0,
    );
    return { totalOut, totalIn, totalAmount };
  }, [displayRows]);

  const allRowKeys = useMemo(
    () => displayRows.map((row) => getPrescriptionRowKey(row)).filter(Boolean),
    [displayRows],
  );
  const allSelected =
    allRowKeys.length > 0 && allRowKeys.every((key) => selectedRowKeys.includes(key));
  const someSelected =
    selectedRowKeys.length > 0 && !allSelected;

  const updateStagingRow = (
    rowKey: string,
    patch: Partial<StagingPrescriptionDrug>,
  ) => {
    setStagingRows((prev) =>
      prev.map((row) =>
        getPrescriptionRowKey(row) === rowKey ? { ...row, ...patch } : row,
      ),
    );
    markFormChanged();
  };

  const removeStagingRow = (rowKey: string) => {
    setStagingRows((prev) =>
      prev.filter((row) => getPrescriptionRowKey(row) !== rowKey),
    );
    setSelectedRowKeys((prev) => prev.filter((key) => key !== rowKey));
    markFormChanged();
  };

  const toggleRowSelected = (rowKey: string, checked: boolean) => {
    setSelectedRowKeys((prev) =>
      checked ? [...prev, rowKey] : prev.filter((key) => key !== rowKey),
    );
  };

  const toggleSelectAll = (checked: boolean) => {
    setSelectedRowKeys(checked ? allRowKeys : []);
  };

  /** 레거시 insertInlineDrugRow — 빈 행 추가 후 즉시 인라인 편집 진입 */
  const handleAddEmptyRow = () => {
    if (editingRowKey != null) {
      toast.error('편집 중인 행을 먼저 저장하거나 취소해 주세요.');
      return;
    }
    const newRow = withPrescriptionTempRowKey({
      drugCode: '',
      drugName: '',
      price: 0,
      outHospitalQuantity: 0,
      inHospitalQuantity: 0,
      pharmaCompanyName: '',
      pharmaCompanyId: 0,
      isDrugInfoExists: null,
    });
    const rowKey = getPrescriptionRowKey(newRow);
    setStagingRows((prev) => [...prev, newRow]);
    setEditingRowKey(rowKey);
    setEditDraft({
      drugCode: '',
      drugName: '',
      price: 0,
      outHospitalQuantity: 0,
      inHospitalQuantity: 0,
      pharmaCompanyName: '',
      pharmaCompanyId: 0,
      isDrugInfoExists: null,
    });
    markFormChanged();
    toast.success('입력용 행이 추가되었습니다.');
  };

  /**
   * 이전 처방 새 창에서 고른 약을 미저장 행으로 추가한다.
   * 약가·제약사는 현재 처방년월 마스터 값, 수량은 새 창에서 입력한 처방량(미입력 0)이다.
   * 저장은 기존 [의약품 일괄 추가](POST /drugs)가 한다.
   */
  const handleAddPreviousDrugs = (additions: PreviousDrugAddition[]) => {
    // 창 사이 메시지는 비동기라, 그사이 이미 들어간 약은 한 번 더 걸러 중복 행을 막는다
    const fresh = additions.filter(({ drug }) => {
      const code = drug.drugCode.trim();
      return !stagedDrugCodes.has(code) && !registeredDrugCodes.has(code);
    });
    if (fresh.length === 0) return;
    setStagingRows((prev) => [
      ...prev,
      ...fresh.map(({ drug, outHospitalQuantity, inHospitalQuantity }) =>
        withPrescriptionTempRowKey({
          drugCode: drug.drugCode.trim(),
          drugName: drug.drugName ?? '',
          price: drug.price ?? 0,
          outHospitalQuantity,
          inHospitalQuantity,
          pharmaCompanyId: drug.pharmaCompanyId ?? 0,
          pharmaCompanyName: drug.pharmaCompanyName ?? '',
          isDrugInfoExists: drug.isDrugInfoExists,
        }),
      ),
    ]);
    markFormChanged();
    toast.success(
      `이전 처방 의약품 ${fresh.length}건을 추가했습니다. [의약품 일괄 추가]로 저장해 주세요.`,
    );
  };

  const previousDrugsWindow = usePreviousDrugsWindowHost({
    prescriptionImageCode,
    stagedDrugCodes,
    registeredDrugCodes,
    onAdd: handleAddPreviousDrugs,
  });

  const openPreviousDrugsWindow = () => {
    if (!previousDrugsWindow.openWindow()) {
      toast.error(
        '팝업이 차단되어 이전 처방 창을 열지 못했습니다. 브라우저에서 팝업을 허용해 주세요.',
      );
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) return;
    const ok = await confirm({
      title: '삭제 확인',
      description: `선택한 의약품 ${selectedRowKeys.length}건을 삭제합니다.`,
      confirmLabel: '삭제',
      variant: 'destructive',
    });
    if (!ok) return;

    const selectedSet = new Set(selectedRowKeys);
    const stagingToRemove = stagingRows.filter((row) =>
      selectedSet.has(getPrescriptionRowKey(row)),
    );
    const savedToDelete = drugs.filter((row) =>
      selectedSet.has(getPrescriptionRowKey(row)),
    );

    try {
      if (stagingToRemove.length > 0) {
        setStagingRows((prev) =>
          prev.filter((row) => !selectedSet.has(getPrescriptionRowKey(row))),
        );
      }
      for (const drug of savedToDelete) {
        if (drug.prescriptionMrId != null) {
          await deleteDrug.mutateAsync(drug.prescriptionMrId);
        }
      }
      setSelectedRowKeys([]);
      markFormChanged();
      toast.success('선택한 의약품을 삭제했습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const autoOcrMeta = getAutoOcrStatusMeta(imageDetail?.ocrStatus);

  usePrescriptionAutoOcrPolling(
    prescriptionImageCode || null,
    effectiveRefYm || null,
    imageDetail?.ocrStatus,
  );

  useEffect(() => {
    if (imageDetail) {
      setRegistrationDate(imageDetail.registrationDate?.slice(0, 10) ?? '');
      setPrescriptionYearMonth(imageDetail.prescriptionYearMonth ?? '');
      setExposed(imageDetail.exposed ?? true);
    }
  }, [imageDetail]);

  useEffect(() => {
    setSelectedRowKeys((prev) => keepSelectedRowKeys(prev, allRowKeys));
  }, [allRowKeys]);

  // 수정·행 추가 후 코드 인풋이 마운트된 뒤에 포커스한다
  useLayoutEffect(() => {
    if (!editingRowKey) return;
    const input = drugCodeInputRef.current;
    if (!input) return;
    input.focus();
    input.select();
  }, [editingRowKey]);

  /** 레거시 QuitButton — 목록 URL 쿼리(조회조건) 복귀 */
  const navigateToList = (options?: { replace?: boolean }) => {
    if (listReturnSearch) {
      navigate({
        to: ROUTE_PATHS.PRESCRIPTION_REVIEW,
        search: listReturnSearch,
        replace: options?.replace,
      } as never);
      return;
    }
    if (!options?.replace && router.history.canGoBack()) {
      router.history.back();
      return;
    }
    navigate({
      to: ROUTE_PATHS.PRESCRIPTION_REVIEW,
      replace: options?.replace,
    } as never);
  };

  const navigateBack = () => navigateToList();

  const handleQuit = () => {
    if (isDirty) {
      setQuitDialogOpen(true);
      return;
    }
    navigateBack();
  };

  const handleSaveImage = async () => {
    if (!prescriptionImageCode) return;
    if (hasUnsavedOcrDrugs) {
      toast.error('저장되지 않은 OCR 의약품이 있습니다. 일괄 추가 후 저장해 주세요.');
      return;
    }
    try {
      await updateImage.mutateAsync({
        prescriptionImageCode,
        data: {
          registrationDate,
          prescriptionYearMonth: prescriptionYearMonth || undefined,
          exposed,
        },
      });
      setIsFormChanged(false);
      toast.success('처방 내역이 수정되었습니다.');
      if (
        prescriptionYearMonth &&
        prescriptionYearMonth !== effectiveRefYm
      ) {
        navigate({
          to: prescriptionReviewDetailPath(prescriptionImageCode),
          search: { refYm: prescriptionYearMonth },
          // 목록 조회조건 state 유지 (삭제·나가기 복원)
          state: location.state,
          replace: true,
        } as never);
      }
    } catch (e) {
      handleApiError(e, toast);
      // 확정 후 노출 변경 등 동시성 에러 시 서버 상태로 재동기화
      void queryClient.invalidateQueries({
        queryKey: prescriptionKeys.imageDetail(prescriptionImageCode),
      });
    }
  };

  const handleDeletePrescription = async () => {
    if (!prescriptionImageCode) return;
    const ok = await confirm({
      title: '처방 삭제',
      description:
        '처방 이미지와 내역이 모두 삭제되며 복구할 수 없습니다. 삭제하시겠습니까?',
      confirmLabel: '삭제',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await deletePrescription.mutateAsync(prescriptionImageCode);
      toast.success('처방 내역이 삭제되었습니다.');
      // 목록 진입 시 조회조건(URL search)을 유지한 채 복귀
      navigateToList({ replace: true });
    } catch (e) {
      handleApiError(e, toast);
      // 동시 편집으로 조건이 바뀐 경우 상세 재조회
      void queryClient.invalidateQueries({
        queryKey: prescriptionKeys.imageDetail(prescriptionImageCode),
      });
    }
  };

  const proceedFinalize = async () => {
    if (!prescriptionImageCode) return;
    try {
      await finalize.mutateAsync(prescriptionImageCode);
      setIsFormChanged(false);
      toast.success('처방 내역이 확정 되었습니다.');
      navigateBack();
    } catch (e) {
      handleApiError(e, toast);
      // 404·미노출(PRESCRIPTION_IMAGE_HIDDEN) 등 — 상세 재조회
      void queryClient.invalidateQueries({
        queryKey: prescriptionKeys.imageDetail(prescriptionImageCode),
      });
    }
  };

  const checkCrossImageAndFinalize = async (
    drugsForCheck: PrescriptionDrugInfoResponse[] = drugs,
  ) => {
    if (!prescriptionImageCode) return;
    try {
      const apiDuplicates = await findDuplicates.mutateAsync(
        prescriptionImageCode,
      );
      const crossImage = buildCrossImageDuplicateDrugs(
        apiDuplicates,
        drugsForCheck,
        prescriptionImageCode,
      );
      if (crossImage.length > 0) {
        setCrossDuplicateItems(crossImage);
        setCrossDuplicateOpen(true);
        return;
      }
      await proceedFinalize();
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleFinalize = async () => {
    if (!prescriptionImageCode) return;

    if (hasUnsavedOcrDrugs) {
      toast.error('의약품 정보가 저장되지 않은 의약품이 있습니다.');
      return;
    }

    if (isDirty) {
      toast.error('변경된 내용을 먼저 저장해 주세요.');
      return;
    }

    if (!drugs.every((drug) => drug.isDrugInfoExists === true)) {
      toast.error('의약품 정보가 존재하지 않는 의약품이 있습니다.');
      return;
    }

    if (duplicatedDrugCodes.length > 0) {
      toast.error(
        `동일한 의약품 코드가 중복되어 있습니다: ${duplicatedDrugCodes.join(', ')}`,
      );
      setWithinDuplicateOpen(true);
      return;
    }

    await checkCrossImageAndFinalize();
  };

  const handleCancelFinalization = async () => {
    if (!prescriptionImageCode) return;
    const ok = await confirm({
      title: '처방확정 취소',
      description: '확정된 처방 내역을 취소하시겠습니까?',
      confirmLabel: '취소하기',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await cancelFinalization.mutateAsync(prescriptionImageCode);
      toast.success('처방 확정이 취소되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleExclude = async (reason: PrescriptionImageExclusionReason) => {
    if (!prescriptionImageCode) return;
    try {
      await excludeImage.mutateAsync({ prescriptionImageCode, reason });
      // 제외 중엔 편집이 잠기므로 저장 안 된 OCR·행 추가는 버린다
      setStagingRows([]);
      setEditingRowKey(null);
      setEditDraft(null);
      setIsFormChanged(false);
      setExclusionDialogOpen(false);
      toast.success('처방 확정 제외로 지정했습니다.');
    } catch (e) {
      handleApiError(e, toast);
      // 동시에 확정된 경우(409) 등 — 서버 상태로 재동기화
      void queryClient.invalidateQueries({
        queryKey: prescriptionKeys.imageDetail(prescriptionImageCode),
      });
    }
  };

  const handleCancelExclusion = async () => {
    if (!prescriptionImageCode) return;
    const ok = await confirm({
      title: '확정 제외 해제',
      description:
        '확정 제외를 해제하면 다시 확정·집계 대상이 됩니다. 해제하시겠습니까?',
      confirmLabel: '해제',
    });
    if (!ok) return;
    try {
      await cancelExclusion.mutateAsync(prescriptionImageCode);
      toast.success('확정 제외를 해제했습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  /** 레거시 runPlainOcr — Document AI process-url + normalizeEditedOcrItems */
  const loadOcrIntoStaging = async () => {
    if (!imageDetail?.prescriptionImageUrl) return;

    const raw = await ocrService.ocrPrescriptionFromUrl({
      imageUrl: imageDetail.prescriptionImageUrl,
    });
    const staging = mapEditedOcrRowsToStagingDrugs(normalizeEditedOcrItems(raw));
    if (staging.length === 0) {
      toast.error('OCR 결과가 없습니다.');
      return;
    }
    setStagingRows(staging);
    markFormChanged();
    toast.success('OCR 요청이 완료되었습니다.');
  };

  const handleOcrRegister = async () => {
    if (!prescriptionImageCode || !imageDetail?.prescriptionImageUrl) {
      toast.error('OCR할 이미지가 없습니다.');
      return;
    }

    const run = async () => {
      setOcrRequesting(true);
      try {
        await loadOcrIntoStaging();
      } catch (e) {
        handleApiError(e, toast);
      } finally {
        setOcrRequesting(false);
      }
    };

    const hasExisting = drugs.length > 0 || stagingRows.length > 0;
    if (hasExisting) {
      const ok = await confirm({
        title: 'OCR 재등록',
        description:
          '의약품이 이미 등록되어 있습니다. 삭제하고 다시 등록하시겠습니까?',
        confirmLabel: '다시 등록',
        variant: 'destructive',
      });
      if (!ok) return;
      try {
        setOcrRequesting(true);
        if (drugs.length > 0) {
          await deleteDrugs.mutateAsync([prescriptionImageCode]);
        }
        setStagingRows([]);
        await loadOcrIntoStaging();
      } catch (e) {
        handleApiError(e, toast);
      } finally {
        setOcrRequesting(false);
      }
      return;
    }

    await run();
  };

  const handleDrugBatchAddition = async () => {
    if (!prescriptionImageCode || stagingRows.length === 0) {
      toast.error('일괄 등록할 의약품이 없습니다.');
      return;
    }
    if (stagingRows.some((row) => !row.drugCode?.trim())) {
      toast.error('의약품 코드가 비어있는 의약품이 있습니다.');
      return;
    }

    const pendingDrugList = stagingRows;
    try {
      await createDrugs.mutateAsync(
        pendingDrugList.map((row) => ({
          prescriptionImageCode,
          drugCode: row.drugCode,
          outHospitalQuantity: row.outHospitalQuantity ?? 0,
          inHospitalQuantity: row.inHospitalQuantity ?? 0,
        })),
      );
      const refreshed = await refetchDrugs();
      const fetched = refreshed.data ?? [];
      const merged = mergeFetchedDrugsWithFallbackByDrugCode(
        fetched,
        pendingDrugList,
      );
      if (effectiveRefYm) {
        queryClient.setQueryData(
          prescriptionKeys.drugs(prescriptionImageCode, effectiveRefYm),
          merged,
        );
      }
      setStagingRows([]);
      markFormChanged();
      toast.success('의약품 등록이 완료되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleDrugMatching = async () => {
    if (stagingRows.length === 0) {
      toast.error('매칭할 의약품이 없습니다.');
      return;
    }
    if (!matchingYearMonth) {
      toast.error('처방 년월 정보가 없습니다.');
      return;
    }

    setDrugMatching(true);
    try {
      const items = stagingRows.map((item) => ({
        drugName: item.drugName ?? '',
        ...(item.drugCode ? { drugCode: item.drugCode } : {}),
        ...(item.pharmaCompanyName
          ? { pharmaCompanyName: item.pharmaCompanyName }
          : {}),
        ...(item.price != null ? { unitPrice: item.price } : {}),
        ...(item.inHospitalQuantity != null
          ? { inQuantity: item.inHospitalQuantity }
          : {}),
        ...(item.outHospitalQuantity != null
          ? { outQuantity: item.outHospitalQuantity }
          : {}),
      }));

      const response = await ocrService.searchDrugMatching({
        items,
        limit: 5,
        yearMonth: matchingYearMonth,
      });

      if (response.metadata?.confidence === 'no_data') {
        toast.error(
          response.metadata.reason ||
            '해당 연월의 마스터 데이터가 없어 매칭할 수 없습니다.',
        );
        return;
      }

      const results = response.results;
      if (results.length === 0) {
        toast.error('매칭 결과가 없습니다.');
        return;
      }

      const matched = applyDrugMatchingResults(stagingRows, results);
      const matchedCount = results.filter((r) => r.candidates?.[0]).length;
      setStagingRows(matched);
      markFormChanged();
      toast.success(`의약품 매칭 완료 (${matchedCount}/${stagingRows.length}건)`);
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setDrugMatching(false);
    }
  };

  const handleStagingDrugCodeBlur = async (row: StagingPrescriptionDrug) => {
    const rowKey = getPrescriptionRowKey(row);
    const drugCode = row.drugCode?.trim();
    if (!drugCode || !effectiveRefYm) return;

    try {
      const found = await searchDrugByCode.mutateAsync({
        drugCode,
        refYm: effectiveRefYm,
      });
      if (!found) {
        updateStagingRow(rowKey, { isDrugInfoExists: false });
        return;
      }
      updateStagingRow(rowKey, {
        drugCode: found.drugCode,
        drugName: found.drugName,
        price: found.price ?? row.price,
        pharmaCompanyName: found.pharmaCompanyName ?? row.pharmaCompanyName,
        pharmaCompanyId: found.pharmaCompanyId ?? row.pharmaCompanyId,
        isDrugInfoExists: true,
      });
    } catch {
      updateStagingRow(rowKey, { isDrugInfoExists: false });
    }
  };

  const openNameSearchForRow = (
    row: StagingPrescriptionDrug,
    options?: { persist?: boolean },
  ) => {
    setNameSearchTargetKey(getPrescriptionRowKey(row));
    setNameSearchPersist(Boolean(options?.persist));
    setNameSearchDraftName(row.drugName ?? '');
    setNameSearchKeyword(
      buildDrugNameSearchKeywordWhenNotEditing(row.drugName) ||
        (row.drugName ?? ''),
    );
    setNameSearchOpen(true);
  };

  const patchLocalSavedDrug = (
    prescriptionMrId: number,
    patch: Partial<PrescriptionDrugInfoResponse>,
  ) => {
    if (!effectiveRefYm || !prescriptionImageCode) return;
    queryClient.setQueryData(
      prescriptionKeys.drugs(prescriptionImageCode, effectiveRefYm),
      (prev: PrescriptionDrugInfoResponse[] | undefined) =>
        (prev ?? []).map((item) =>
          item.prescriptionMrId === prescriptionMrId
            ? { ...item, ...patch }
            : item,
        ),
    );
  };

  /** 레거시 handleEditRow — 저장행 인라인 편집 시작 */
  const handleStartEditRow = (drug: PrescriptionDrugInfoResponse) => {
    const rowKey = getPrescriptionRowKey(drug);
    setEditingRowKey(rowKey);
    setEditDraft({
      drugCode: drug.drugCode ?? '',
      drugName: drug.drugName ?? '',
      price: drug.price ?? 0,
      outHospitalQuantity: drug.outHospitalQuantity ?? 0,
      inHospitalQuantity: drug.inHospitalQuantity ?? 0,
      pharmaCompanyName: drug.pharmaCompanyName ?? '',
      pharmaCompanyId: drug.pharmaCompanyId ?? 0,
      isDrugInfoExists: drug.isDrugInfoExists ?? true,
    });
  };

  const handleCancelEditRow = (sourceRow?: StagingPrescriptionDrug) => {
    const rowKey = editingRowKey;
    const draft = editDraft;
    setEditingRowKey(null);
    setEditDraft(null);

    // 레거시: 신규 빈 행 취소 시 목록에서 제거
    if (
      rowKey &&
      sourceRow &&
      isStagingDrugRow(sourceRow) &&
      !draft?.drugCode &&
      !draft?.drugName &&
      Number(draft?.outHospitalQuantity ?? 0) === 0 &&
      Number(draft?.inHospitalQuantity ?? 0) === 0
    ) {
      removeStagingRow(rowKey);
    }
  };

  const updateEditDraft = (
    patch: Partial<NonNullable<typeof editDraft>>,
  ) => {
    setEditDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  /** 레거시 handleSaveRow — 저장행 PATCH / 신규 행 즉시 POST */
  const handleSaveEditRow = async (drug: StagingPrescriptionDrug) => {
    if (!editDraft || !imageDetail) return;
    if (!editDraft.drugCode.trim()) {
      toast.error('의약품 코드를 입력해 주세요.');
      return;
    }

    setRowSaving(true);
    try {
      if (isStagingDrugRow(drug)) {
        // 레거시 persistInlineDrugRow(신규) — 단건 POST 후 목록 재조회
        await createDrug.mutateAsync({
          prescriptionImageCode,
          userId: imageDetail.userId,
          clinicId: imageDetail.clinicId,
          drugCode: editDraft.drugCode.trim(),
          pharmaCompanyId: toNullablePharmaCompanyId(
            editDraft.pharmaCompanyId,
          ),
          prescriptionYearMonth: imageDetail.prescriptionYearMonth,
          prescriptionRegisteredYearMonth:
            imageDetail.prescriptionRegisteredYearMonth,
          settlementYearMonth: imageDetail.settlementYearMonth,
          price: editDraft.price ?? 0,
          outHospitalQuantity: editDraft.outHospitalQuantity ?? 0,
          inHospitalQuantity: editDraft.inHospitalQuantity ?? 0,
        });
        removeStagingRow(getPrescriptionRowKey(drug));
        await refetchDrugs();
        markFormChanged();
        setEditingRowKey(null);
        setEditDraft(null);
        toast.success('행 정보가 수정되었습니다.');
        return;
      }

      if (drug.prescriptionMrId == null) return;

      await updateDrug.mutateAsync({
        prescriptionMrId: drug.prescriptionMrId,
        prescriptionImageCode,
        refYm: effectiveRefYm,
        data: buildUpdatePrescriptionDrugRequest({
          drugCode: editDraft.drugCode.trim(),
          pharmaCompanyId: editDraft.pharmaCompanyId,
          price: editDraft.price ?? 0,
          outHospitalQuantity: editDraft.outHospitalQuantity ?? 0,
          inHospitalQuantity: editDraft.inHospitalQuantity ?? 0,
        }),
      });
      patchLocalSavedDrug(drug.prescriptionMrId, {
        drugCode: editDraft.drugCode.trim(),
        drugName: editDraft.drugName,
        price: editDraft.price ?? 0,
        outHospitalQuantity: editDraft.outHospitalQuantity ?? 0,
        inHospitalQuantity: editDraft.inHospitalQuantity ?? 0,
        pharmaCompanyName: editDraft.pharmaCompanyName,
        pharmaCompanyId: editDraft.pharmaCompanyId,
        isDrugInfoExists: editDraft.isDrugInfoExists !== false,
      });
      markFormChanged();
      setEditingRowKey(null);
      setEditDraft(null);
      toast.success('행 정보가 수정되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setRowSaving(false);
    }
  };

  /**
   * 레거시 handleManualDrugLookup(drugCode).
   * 저장행·비편집: persist / 편집 중·staging: local draft
   */
  const handleDrugCodeSearch = async (
    drug: StagingPrescriptionDrug,
    options?: { persist?: boolean; draftCode?: string },
  ) => {
    const rowKey = getPrescriptionRowKey(drug);
    const isEditing = editingRowKey === rowKey && editDraft;
    const drugCode = (
      options?.draftCode ??
      (isEditing ? editDraft?.drugCode : drug.drugCode) ??
      ''
    ).trim();

    if (!drugCode) {
      toast.error('의약품 코드를 입력해 주세요.');
      return;
    }
    if (!effectiveRefYm) {
      toast.error('처방 년월 정보가 없습니다.');
      return;
    }

    try {
      const found = await searchDrugByCode.mutateAsync({
        drugCode,
        refYm: effectiveRefYm,
      });
      const next = {
        drugCode: found.drugCode,
        drugName: found.drugName,
        price: found.price ?? 0,
        pharmaCompanyName: found.pharmaCompanyName ?? '',
        pharmaCompanyId: found.pharmaCompanyId ?? 0,
        isDrugInfoExists: true as boolean | null,
      };

      if (isStagingDrugRow(drug) || isEditing) {
        if (isEditing) {
          updateEditDraft({
            ...next,
            outHospitalQuantity: editDraft!.outHospitalQuantity,
            inHospitalQuantity: editDraft!.inHospitalQuantity,
          });
        } else {
          updateStagingRow(rowKey, next);
        }
        toast.success('의약품 정보를 불러왔습니다.');
        return;
      }

      if (options?.persist && drug.prescriptionMrId != null) {
        await updateDrug.mutateAsync({
          prescriptionMrId: drug.prescriptionMrId,
          prescriptionImageCode,
          refYm: effectiveRefYm,
          data: buildUpdatePrescriptionDrugRequest({
            drugCode: next.drugCode,
            pharmaCompanyId: next.pharmaCompanyId,
            price: next.price,
            outHospitalQuantity: drug.outHospitalQuantity ?? 0,
            inHospitalQuantity: drug.inHospitalQuantity ?? 0,
          }),
        });
        patchLocalSavedDrug(drug.prescriptionMrId, {
          ...next,
          isDrugInfoExists: true,
        });
        markFormChanged();
        toast.success('의약품 정보를 적용했습니다.');
      }
    } catch (e) {
      handleApiError(e, toast);
      if (isEditing) {
        updateEditDraft({
          drugName: '',
          price: 0,
          pharmaCompanyName: '',
          pharmaCompanyId: 0,
          isDrugInfoExists: false,
        });
      } else if (isStagingDrugRow(drug)) {
        updateStagingRow(rowKey, {
          drugName: '',
          price: 0,
          pharmaCompanyName: '',
          pharmaCompanyId: 0,
          isDrugInfoExists: false,
        });
      }
    }
  };

  const applyNameSearchSelection = async (selected: DrugCodeSearchResponse) => {
    if (!nameSearchTargetKey) return;

    const targetKey = nameSearchTargetKey;
    const normalized = normalizeDrugCodeSearchResponse(selected);
    // 이름 검색은 코드·명만 오고 제약사 ID가 비는 경우가 있어, 코드 조회로 보완한다
    if (!normalized.pharmaCompanyId && normalized.drugCode && effectiveRefYm) {
      try {
        const found = await searchDrugByCode.mutateAsync({
          drugCode: normalized.drugCode,
          refYm: effectiveRefYm,
        });
        const lookedUp = normalizeDrugCodeSearchResponse(found);
        if (lookedUp.pharmaCompanyId) {
          normalized.pharmaCompanyId = lookedUp.pharmaCompanyId;
        }
        if (!normalized.pharmaCompanyName) {
          normalized.pharmaCompanyName = lookedUp.pharmaCompanyName;
        }
        if (!normalized.price) {
          normalized.price = lookedUp.price;
        }
      } catch {
        // 레거시와 같이 ID가 없어도 저장은 막지 않고 null로 보낸다
      }
    }

    const next = {
      drugCode: normalized.drugCode,
      drugName: normalized.drugName,
      price: normalized.price,
      pharmaCompanyName: normalized.pharmaCompanyName,
      pharmaCompanyId: normalized.pharmaCompanyId,
      isDrugInfoExists: true as boolean | null,
    };

    const shouldPatchDraft = editingRowKey === targetKey;
    const staging = stagingRows.find(
      (row) => getPrescriptionRowKey(row) === targetKey,
    );
    const saved = drugs.find(
      (row) => getPrescriptionRowKey(row) === targetKey,
    );

    if (shouldPatchDraft || staging) {
      // 편집 중이면 draft·staging을 같이 채워, 코드·명만 복사되고 ID가 남는 일을 막는다
      if (shouldPatchDraft) {
        setEditDraft((prev) =>
          prev
            ? {
                ...prev,
                ...next,
                outHospitalQuantity: prev.outHospitalQuantity,
                inHospitalQuantity: prev.inHospitalQuantity,
              }
            : prev,
        );
      }
      if (staging) {
        updateStagingRow(targetKey, next);
      }
      toast.success('의약품 명 검색 결과를 반영했습니다.');
      return;
    }

    if (nameSearchPersist && saved?.prescriptionMrId != null) {
      try {
        await updateDrug.mutateAsync({
          prescriptionMrId: saved.prescriptionMrId,
          prescriptionImageCode,
          refYm: effectiveRefYm,
          data: buildUpdatePrescriptionDrugRequest({
            drugCode: next.drugCode,
            pharmaCompanyId: next.pharmaCompanyId,
            price: next.price,
            outHospitalQuantity: saved.outHospitalQuantity ?? 0,
            inHospitalQuantity: saved.inHospitalQuantity ?? 0,
          }),
        });
        patchLocalSavedDrug(saved.prescriptionMrId, {
          ...next,
          isDrugInfoExists: true,
        });
        markFormChanged();
        toast.success('의약품 정보를 저장했습니다.');
      } catch (e) {
        handleApiError(e, toast);
      }
    }
  };

  const isOcrBusy =
    ocrRequesting ||
    createDrugs.isPending ||
    deleteDrugs.isPending ||
    editedOcrComparing ||
    comparisonApplying ||
    drugMatching;

  // 레거시 MainLayout loading={... || isRequestingEditedOcrComparison}
  useEffect(() => {
    const busy =
      ocrRequesting ||
      editedOcrComparing ||
      comparisonApplying ||
      ocrEditorPreparing ||
      xlsxUploading ||
      finalize.isPending ||
      cancelFinalization.isPending;
    setGlobalLoading(
      busy,
      editedOcrComparing
        ? '편집 OCR 요청 중...'
        : ocrEditorPreparing
          ? '편집 영역을 준비하는 중...'
          : ocrRequesting
            ? 'OCR 요청 중...'
            : '처리 중...',
    );
    return () => setGlobalLoading(false);
  }, [
    ocrRequesting,
    editedOcrComparing,
    comparisonApplying,
    ocrEditorPreparing,
    xlsxUploading,
    finalize.isPending,
    cancelFinalization.isPending,
    setGlobalLoading,
  ]);

  // 레거시 sessionStorage OCR 좌표 복원
  useEffect(() => {
    const sourceImageUrl = imageDetail?.prescriptionImageUrl;
    const storageKey = buildOcrCoordinateStorageKey(
      prescriptionImageCode,
      OCR_COORDINATE_STORAGE_PREFIX.prescriptionReview,
    );
    const stored = loadOcrCoordinatesFromSession(storageKey, sourceImageUrl);
    if (!stored) return;
    setOcrSourceCorners(stored.points);
    setOcrSourceBbox(stored.bbox ?? getBoundingBoxFromPoints(stored.points));
  }, [imageDetail?.prescriptionImageUrl, prescriptionImageCode]);

  // 좌표 변경 시 sessionStorage 저장
  useEffect(() => {
    const sourceImageUrl = imageDetail?.prescriptionImageUrl;
    if (!sourceImageUrl || !ocrSourceCorners || ocrSourceCorners.length !== 4) {
      return;
    }
    const storageKey = buildOcrCoordinateStorageKey(
      prescriptionImageCode,
      OCR_COORDINATE_STORAGE_PREFIX.prescriptionReview,
    );
    saveOcrCoordinatesToSession(storageKey, {
      sourceImageUrl,
      points: ocrSourceCorners,
      bbox: ocrSourceBbox ?? getBoundingBoxFromPoints(ocrSourceCorners),
    });
  }, [
    imageDetail?.prescriptionImageUrl,
    ocrSourceBbox,
    ocrSourceCorners,
    prescriptionImageCode,
  ]);

  // 비교 미리보기 blob URL 정리
  useEffect(() => {
    return () => {
      if (editedOcrRequestPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(editedOcrRequestPreviewUrl);
      }
    };
  }, [editedOcrRequestPreviewUrl]);

  const handleDownloadXlsxTemplate = () => {
    exportXlsxTemplate(
      PRESCRIPTION_XLSX_HEADER_MAP,
      '처방 내역 의약품 테이블 양식.xlsx',
    );
  };

  const resetXlsxUploadDialog = () => {
    if (xlsxInputRef.current) {
      xlsxInputRef.current.value = '';
    }
  };

  const handleXlsxUpload = async (file: File) => {
    if (!prescriptionImageCode) return;
    setXlsxUploading(true);
    try {
      const rows = await importFromXlsx(file, PRESCRIPTION_XLSX_HEADER_MAP);
      const mapped = mapXlsxRowsToCreateRequests(rows, prescriptionImageCode);
      if (!mapped.length) {
        toast.error('업로드할 의약품 행이 없습니다.');
        return;
      }
      await createDrugs.mutateAsync(mapped);
      markFormChanged();
      toast.success('XLSX 의약품 등록이 완료되었습니다.');
      setXlsxUploadDialogOpen(false);
      resetXlsxUploadDialog();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'XLSX 업로드에 실패했습니다.';
      toast.error(message);
    } finally {
      setXlsxUploading(false);
    }
  };

  const handleAutoMatchingRetry = async () => {
    if (!prescriptionImageCode) return;
    try {
      await autoMatchingRetry.mutateAsync(prescriptionImageCode);
      toast.success('다시 매칭 요청을 보냈습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const openOcrEditor = async () => {
    if (!imageDetail?.prescriptionImageUrl) {
      toast.error('처방전 이미지 URL이 없습니다.');
      return;
    }
    setEditedOcrImages([]);
    setOcrEditorOpen(true);

    const sourceImageUrl = imageDetail.prescriptionImageUrl;
    const storageKey = buildOcrCoordinateStorageKey(
      prescriptionImageCode,
      OCR_COORDINATE_STORAGE_PREFIX.prescriptionReview,
    );
    const cached = loadOcrCoordinatesFromSession(storageKey, sourceImageUrl);
    if (cached) {
      setOcrSourceCorners(cached.points);
      setOcrSourceBbox(
        cached.bbox ?? getBoundingBoxFromPoints(cached.points),
      );
      return;
    }

    if (ocrSourceCorners && ocrSourceCorners.length === 4) {
      return;
    }

    setOcrEditorPreparing(true);
    try {
      const corners = await preprocessService.getCornersFromUrl(sourceImageUrl);
      const points = getInitialOcrCornerPoints(corners);
      if (points.length === 4) {
        const bbox = getBoundingBoxFromPoints(points);
        setOcrSourceCorners(points);
        setOcrSourceBbox(bbox);
        saveOcrCoordinatesToSession(storageKey, {
          sourceImageUrl,
          points,
          bbox,
        });
      } else {
        setOcrSourceCorners(null);
        setOcrSourceBbox(null);
      }
    } catch {
      setOcrSourceCorners(null);
      setOcrSourceBbox(null);
    } finally {
      setOcrEditorPreparing(false);
    }
  };

  const closeOcrEditor = () => {
    const meta = editedOcrImages[0]?.ocrMeta;
    const live = ocrLiveSelection;
    if (meta?.corners?.length === 4) {
      setOcrSourceCorners(meta.corners);
      setOcrSourceBbox(meta.bbox ?? getBoundingBoxFromPoints(meta.corners));
    } else if (live?.corners?.length === 4) {
      setOcrSourceCorners(live.corners);
      setOcrSourceBbox(live.bbox);
    }
    setOcrEditorOpen(false);
    setEditedOcrImages([]);
    setOcrLiveSelection(null);
  };

  const runEditedOcrComparison = async () => {
    const imageUrl = imageDetail?.prescriptionImageUrl;
    // 적용(Apply) 없이도 편집 중 선택 영역으로 OCR 요청 가능
    const meta = editedOcrImages[0]?.ocrMeta;
    const corners = meta?.corners ?? ocrLiveSelection?.corners;
    const bbox = meta?.bbox ?? ocrLiveSelection?.bbox;
    const nextPreviewUrl =
      editedOcrImages[0]?.preview || ocrLiveSelection?.previewUrl || '';

    if (
      !imageUrl ||
      !bbox ||
      !Array.isArray(corners) ||
      corners.length !== 4
    ) {
      toast.error('OCR 좌표 영역을 먼저 선택해 주세요.');
      return;
    }

    setOcrSourceCorners(corners);
    setOcrSourceBbox(bbox);

    setEditedOcrComparing(true);
    try {
      // 업로더 언마운트 시 blob revoke에 대비해 미리보기 URL을 복제
      let previewForModal = nextPreviewUrl || null;
      if (nextPreviewUrl?.startsWith('blob:')) {
        try {
          const blob = await (await fetch(nextPreviewUrl)).blob();
          previewForModal = URL.createObjectURL(blob);
        } catch {
          previewForModal = null;
        }
      }

      const payload = { imageUrl, corners, bbox };
      const [defaultResult, geminiResult] = await Promise.allSettled([
        ocrService.ocrPrescriptionFromUrl(payload),
        ocrService.ocrPrescriptionFromUrlGemini(payload),
      ]);

      const normalizedDefault =
        defaultResult.status === 'fulfilled'
          ? normalizeEditedOcrItems(defaultResult.value)
          : [];
      const normalizedGemini =
        geminiResult.status === 'fulfilled'
          ? normalizeEditedOcrItems(geminiResult.value)
          : [];

      if (normalizedDefault.length === 0 && normalizedGemini.length === 0) {
        if (previewForModal?.startsWith('blob:')) {
          URL.revokeObjectURL(previewForModal);
        }
        throw new Error('Document AI와 Gemini OCR 결과를 모두 가져오지 못했습니다.');
      }

      setEditedOcrRequestPreviewUrl((prev) => {
        if (prev?.startsWith('blob:') && prev !== previewForModal) {
          URL.revokeObjectURL(prev);
        }
        return previewForModal;
      });
      setComparisonResults({ default: normalizedDefault, gemini: normalizedGemini });
      setComparisonOpen(true);
      closeOcrEditor();

      if (defaultResult.status === 'rejected' || geminiResult.status === 'rejected') {
        toast.error('일부 OCR 요청이 실패했습니다. 조회된 결과만 비교 후 선택해 주세요.');
      } else {
        toast.success('OCR 비교 결과를 불러왔습니다.');
      }
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setEditedOcrComparing(false);
    }
  };

  const applyEditedOcrResult = async (source: 'default' | 'gemini') => {
    if (!prescriptionImageCode) return;
    const selected = comparisonResults[source] ?? [];
    if (!selected.length) {
      toast.error('적용할 OCR 결과가 없습니다.');
      return;
    }

    const applyRows = () => {
      const staging = mapEditedOcrRowsToStagingDrugs(selected);
      if (!staging.length) {
        toast.error('등록 가능한 의약품이 없습니다.');
        return;
      }
      setStagingRows(staging);
      markFormChanged();
      toast.success(
        `${source === 'gemini' ? 'Gemini' : 'Document AI'} OCR 결과를 검토 목록에 추가했습니다.`,
      );
      setComparisonOpen(false);
    };

    if (drugs.length > 0 || stagingRows.length > 0) {
      const ok = await confirm({
        title: 'OCR 재등록',
        description:
          '의약품이 이미 등록되어 있습니다. 삭제하고 다시 등록하시겠습니까?',
        confirmLabel: '다시 등록',
        variant: 'destructive',
      });
      if (!ok) return;
      try {
        setComparisonApplying(true);
        if (drugs.length > 0) {
          await deleteDrugs.mutateAsync([prescriptionImageCode]);
        }
        applyRows();
      } catch (e) {
        handleApiError(e, toast);
      } finally {
        setComparisonApplying(false);
      }
      return;
    }

    applyRows();
  };

  if (!prescriptionImageCode) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        잘못된 처방 경로입니다. 목록에서 다시 선택해 주세요.
      </div>
    );
  }

  if (imageLoading) {
    return (
      <div className="py-16">
        <LoadingSpinner tip="처방 정보를 불러오는 중..." />
      </div>
    );
  }

  if (!imageDetail) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        처방 정보를 찾을 수 없습니다.
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={FileText}
        title="처방내역 상세"
        description="처방 이미지 검수·수정 및 확정"
        actions={
          <ToolbarRow gap={8}>
            <MenuWriteGate>
              {!isExcluded ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={xlsxUploading}
                  onClick={() => setXlsxUploadDialogOpen(true)}
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  XLSX 업로드
                </Button>
              ) : null}
              {isExcluded ? (
                <Button
                  variant="secondary"
                  className="bg-ruda-warning text-white shadow-sm hover:bg-ruda-warning/90"
                  onClick={() => void handleCancelExclusion()}
                  disabled={cancelExclusion.isPending}
                >
                  확정 제외 해제
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  className="bg-ruda-warning text-white shadow-sm hover:bg-ruda-warning/90"
                  onClick={() => setExclusionDialogOpen(true)}
                  disabled={!canExclude || excludeImage.isPending}
                  title={
                    canExclude
                      ? undefined
                      : '확정된 처방은 확정 취소 후 제외할 수 있습니다.'
                  }
                >
                  확정 제외
                </Button>
              )}
              {/* 확정된 건만 처방확정 취소 노출 */}
              {isFinalized ? (
                <Button
                  variant="destructive"
                  onClick={handleCancelFinalization}
                  disabled={cancelFinalization.isPending}
                >
                  처방확정 취소
                </Button>
              ) : null}
              {canShowDeletePrescription ? (
                <Button
                  variant="destructive"
                  onClick={() => void handleDeletePrescription()}
                  disabled={
                    !canDeletePrescription || deletePrescription.isPending
                  }
                >
                  처방 삭제
                </Button>
              ) : null}
              {showSaveButton ? (
                <Button
                  variant="outline"
                  onClick={() => void handleSaveImage()}
                  disabled={updateImage.isPending}
                >
                  수정
                </Button>
              ) : null}
              <Button
                onClick={() => void handleFinalize()}
                disabled={
                  !canFinalize ||
                  finalize.isPending ||
                  findDuplicates.isPending
                }
              >
                처방내역 확정
              </Button>
            </MenuWriteGate>
            <Button variant="outline" onClick={handleQuit}>
              나가기
            </Button>
          </ToolbarRow>
        }
      />

      <Card>
        <CardContent className="grid gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
          {/* 레거시 Form: disabled — 수정 불가 */}
          <Field label="사용자 이메일">
            <Input disabled value={imageDetail.userEmail || '-'} />
          </Field>
          <Field label="대표자명">
            <Input disabled value={imageDetail.ceoName || '-'} />
          </Field>
          <Field label="전화번호">
            <Input disabled value={imageDetail.userPhoneNumber || '-'} />
          </Field>
          <Field label="처방내역 이미지 구분 코드">
            <Input disabled value={imageDetail.prescriptionImageCode || '-'} />
          </Field>
          <DownloadWithPreview
            label="처방내역 이미지"
            src={imageDetail.prescriptionImageUrl}
            downloadName={`${imageDetail.clinicName || 'clinic'}_${imageDetail.prescriptionImageCode}`}
          />
          {/* 레거시: 등록일자·처방 년 월만 편집 가능 */}
          <Field label="등록일자" htmlFor="reg-date">
            <DatePicker
              id="reg-date"
              value={registrationDate}
              onChange={setRegistrationDate}
              disabled={readOnly || isExcluded}
            />
          </Field>
          <Field label="처방 등록 년 월">
            <MonthPicker
              disabled
              value={imageDetail.prescriptionRegisteredYearMonth ?? ''}
              placeholder="-"
            />
          </Field>
          <Field label="병의원명">
            <Input disabled value={imageDetail.clinicName || '-'} />
          </Field>
          <Field label="처방 년 월" htmlFor="rx-ym">
            <MonthPicker
              id="rx-ym"
              placeholder="년월 선택"
              value={prescriptionYearMonth}
              onChange={setPrescriptionYearMonth}
              disabled={readOnly || isExcluded}
            />
          </Field>
          <Field label="정산 년월">
            <MonthPicker
              disabled
              value={imageDetail.settlementYearMonth ?? ''}
              placeholder="-"
            />
          </Field>
          <Field label="관리자 확인 상태">
            <Input
              disabled
              value={getAdminCheckStatusLabel(imageDetail.adminCheckStatus)}
            />
          </Field>
          {isExcluded ? (
            <Field label="확정 제외">
              <Input
                disabled
                value={[
                  getExclusionReasonLabel(imageDetail.exclusionReason),
                  formatDateTimeMinute(imageDetail.excludedAt),
                  imageDetail.excludedByName,
                ]
                  .filter((v) => v && v !== '-')
                  .join(' · ')}
              />
            </Field>
          ) : null}
          <Field label="처방 등록">
            <Input
              disabled
              value={
                imageDetail.registrationSource === 'USER'
                  ? '앱'
                  : imageDetail.registrationSource === 'ADMIN'
                    ? '어드민'
                    : '-'
              }
            />
          </Field>
          <Field label="노출여부">
            <Select
              value={exposed ? 'true' : 'false'}
              onValueChange={(value) => setExposed(value === 'true')}
              disabled={readOnly || !canEditExposed}
            >
              <SelectTrigger aria-label="노출여부">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">노출</SelectItem>
                <SelectItem value="false">미노출</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="자동 매칭 상태">
            <div className="flex flex-wrap items-center gap-2">
              <Input
                disabled
                className="w-40"
                value={autoOcrMeta.label}
              />
              <MenuWriteGate>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={
                    !imageDetail.ocrStatus ||
                    !AUTO_OCR_RETRYABLE_STATUSES.has(
                      imageDetail.ocrStatus as never,
                    ) ||
                    autoMatchingRetry.isPending
                  }
                  onClick={() => void handleAutoMatchingRetry()}
                >
                  다시 매칭하기
                </Button>
              </MenuWriteGate>
            </div>
          </Field>
        </CardContent>
      </Card>

      {/* 제외 중엔 처방약 편집이 서버에서 409 로 막히므로 이 영역의 쓰기 버튼을 숨긴다 */}
      <MenuAccessContext.Provider value={editAccess}>
        {isExcluded ? (
          <p className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            확정 제외된 처방입니다. 의약품 편집·이미지 수정·확정을 하려면 먼저
            확정 제외를 해제해 주세요.
          </p>
        ) : null}
        <div className="space-y-3">
          <div className="flex flex-row flex-wrap items-center justify-between gap-3">
            <div className="space-y-2">
              <h3 className="flex items-center gap-2 text-lg font-semibold">
                의약품 목록
                {hasUnsavedOcrDrugs ? (
                  <Badge variant="secondary">미등록 {stagingRows.length}건</Badge>
                ) : null}
              </h3>
              {/* 행 배경색 의미 — 중복 경고 / 미저장 OCR·행추가 구분 */}
              <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block h-3 w-3 rounded-sm border border-amber-200 bg-amber-50"
                    aria-hidden
                  />
                  동일 의약품 코드 중복
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="inline-block h-3 w-3 rounded-sm border border-sky-200 bg-sky-50"
                    aria-hidden
                  />
                  미저장 행 (OCR·행 추가, 일괄 추가 전)
                </span>
              </div>
            </div>
            <MenuWriteGate>
              <ToolbarRow gap={8}>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openPreviousDrugsWindow}
                  disabled={!prescriptionImageCode}
                >
                  <History className="mr-2 h-4 w-4" />
                  이전 처방 불러오기
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={selectedRowKeys.length === 0 || deleteDrug.isPending}
                  onClick={() => void handleDeleteSelected()}
                >
                  선택 삭제
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddEmptyRow}
                  disabled={editingRowKey != null}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  행 추가
                </Button>
              </ToolbarRow>
            </MenuWriteGate>
          </div>

          <DataCard>
            {drugsLoading ? (
              <div className="py-12">
                <LoadingSpinner tip="의약품 목록을 불러오는 중..." />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-14">
                      <Checkbox
                        checked={
                          allSelected
                            ? true
                            : someSelected
                              ? 'indeterminate'
                              : false
                        }
                        onCheckedChange={(checked) =>
                          toggleSelectAll(checked === true)
                        }
                        aria-label="전체 선택"
                      />
                    </TableHead>
                    <TableHead className="min-w-40">코드</TableHead>
                    <TableHead>의약품명</TableHead>
                    <TableHead>가격</TableHead>
                    <TableHead className="min-w-28">원외</TableHead>
                    <TableHead className="min-w-28">원내</TableHead>
                    <TableHead>제약사</TableHead>
                    <TableHead>정보</TableHead>
                    <TableHead>합계</TableHead>
                    <TableHead className="w-24">작업</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        className="py-8 text-center text-muted-foreground"
                      >
                        등록된 의약품이 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayRows.map((drug) => {
                      const rowKey = getPrescriptionRowKey(drug);
                      const isStaging = isStagingDrugRow(drug);
                      const isEditing =
                        editingRowKey === rowKey && editDraft != null;
                      // READ 전용에서는 인라인 편집·staging 입력 UI를 숨긴다
                      const showEditUi = canWrite && (isStaging || isEditing);
                      const view = isEditing && canWrite ? editDraft! : drug;
                      const anotherRowEditing =
                        editingRowKey != null && editingRowKey !== rowKey;
                      const total =
                        (view.price ?? 0) *
                        ((view.outHospitalQuantity ?? 0) +
                          (view.inHospitalQuantity ?? 0));
                      const isDuplicate =
                        view.drugCode &&
                        duplicatedDrugCodes.includes(view.drugCode);
                      return (
                        <TableRow
                          key={rowKey}
                          className={
                            isDuplicate
                              ? 'bg-amber-50'
                              : isStaging
                                ? 'bg-sky-50'
                                : undefined
                          }
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedRowKeys.includes(rowKey)}
                              onCheckedChange={(checked) =>
                                toggleRowSelected(rowKey, checked === true)
                              }
                              aria-label={`${view.drugCode || rowKey} 선택`}
                            />
                          </TableCell>
                          <TableCell className="min-w-40">
                            {showEditUi ? (
                              <div className="flex w-full min-w-0 items-center gap-1">
                                <Input
                                  ref={isEditing ? drugCodeInputRef : undefined}
                                  aria-label="의약품 코드"
                                  className="h-8 min-w-0 flex-1"
                                  value={view.drugCode ?? ''}
                                  onChange={(e) => {
                                    if (isEditing) {
                                      updateEditDraft({
                                        drugCode: e.target.value,
                                      });
                                    } else {
                                      updateStagingRow(rowKey, {
                                        drugCode: e.target.value,
                                      });
                                    }
                                  }}
                                  onBlur={() => {
                                    if (isStaging) {
                                      void handleStagingDrugCodeBlur(drug);
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (!isSearchEnterKey(e)) return;
                                    e.preventDefault();
                                    void handleDrugCodeSearch(drug, {
                                      draftCode: e.currentTarget.value,
                                    });
                                  }}
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="shrink-0"
                                  disabled={
                                    !String(view.drugCode ?? '').trim() ||
                                    searchDrugByCode.isPending
                                  }
                                  onClick={() =>
                                    void handleDrugCodeSearch(drug, {
                                      draftCode: isEditing
                                        ? editDraft?.drugCode
                                        : undefined,
                                    })
                                  }
                                >
                                  검색
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <span>
                                  {drug.drugCode || (
                                    <span className="text-muted-foreground">
                                      의약품 코드 입력
                                    </span>
                                  )}
                                </span>
                                <MenuWriteGate>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    disabled={
                                      !drug.drugCode ||
                                      anotherRowEditing ||
                                      searchDrugByCode.isPending ||
                                      updateDrug.isPending
                                    }
                                    onClick={() =>
                                      void handleDrugCodeSearch(drug, {
                                        persist: true,
                                      })
                                    }
                                  >
                                    검색
                                  </Button>
                                </MenuWriteGate>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {showEditUi ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  aria-label="의약품명"
                                  className="h-8 min-w-32"
                                  value={view.drugName ?? ''}
                                  onChange={(e) => {
                                    if (isEditing) {
                                      updateEditDraft({
                                        drugName: e.target.value,
                                      });
                                    } else {
                                      updateStagingRow(rowKey, {
                                        drugName: e.target.value,
                                      });
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (!isSearchEnterKey(e)) return;
                                    e.preventDefault();
                                    const drugName = e.currentTarget.value.trim();
                                    if (!drugName) return;
                                    openNameSearchForRow(
                                      isEditing
                                        ? { ...drug, ...editDraft, drugName }
                                        : { ...drug, drugName },
                                    );
                                  }}
                                />
                                <Button
                                  type="button"
                                  size="sm"
                                  className="ruda-button"
                                  disabled={!String(view.drugName ?? '').trim()}
                                  onClick={() =>
                                    openNameSearchForRow(
                                      isEditing
                                        ? { ...drug, ...editDraft }
                                        : drug,
                                    )
                                  }
                                >
                                  검색
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <span className="max-w-48 truncate">
                                  {drug.drugName || '-'}
                                </span>
                                <MenuWriteGate>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    disabled={
                                      !drug.drugName ||
                                      anotherRowEditing ||
                                      updateDrug.isPending
                                    }
                                    onClick={() =>
                                      openNameSearchForRow(drug, {
                                        persist: true,
                                      })
                                    }
                                  >
                                    검색
                                  </Button>
                                </MenuWriteGate>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>{formatPrice(view.price)}</TableCell>
                          <TableCell className="min-w-28">
                            {showEditUi ? (
                              <QuantityInput
                                aria-label="원외 수량"
                                className={QUANTITY_INPUT_CLASS_NAME}
                                value={view.outHospitalQuantity ?? 0}
                                onChange={(value) => {
                                  if (isEditing) {
                                    updateEditDraft({
                                      outHospitalQuantity: value,
                                    });
                                  } else {
                                    updateStagingRow(rowKey, {
                                      outHospitalQuantity: value,
                                    });
                                  }
                                }}
                              />
                            ) : (
                              drug.outHospitalQuantity
                            )}
                          </TableCell>
                          <TableCell className="min-w-28">
                            {showEditUi ? (
                              <QuantityInput
                                aria-label="원내 수량"
                                className={QUANTITY_INPUT_CLASS_NAME}
                                value={view.inHospitalQuantity ?? 0}
                                onChange={(value) => {
                                  if (isEditing) {
                                    updateEditDraft({
                                      inHospitalQuantity: value,
                                    });
                                  } else {
                                    updateStagingRow(rowKey, {
                                      inHospitalQuantity: value,
                                    });
                                  }
                                }}
                              />
                            ) : (
                              drug.inHospitalQuantity
                            )}
                          </TableCell>
                          <TableCell>
                            {view.pharmaCompanyName || '-'}
                          </TableCell>
                          <TableCell>
                            {view.isDrugInfoExists === true ? (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            ) : view.isDrugInfoExists === false ? (
                              <AlertCircle className="h-4 w-4 text-red-500" />
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                의약품 일괄 추가 필요
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{formatPrice(total)}</TableCell>
                          <TableCell>
                            <MenuWriteGate>
                              <div className="flex gap-1">
                                {isEditing ? (
                                  <>
                                    <Button
                                      size="sm"
                                      className="ruda-button"
                                      disabled={
                                        rowSaving ||
                                        updateDrug.isPending ||
                                        createDrug.isPending
                                      }
                                      onClick={() => void handleSaveEditRow(drug)}
                                    >
                                      저장
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={rowSaving}
                                      onClick={() => handleCancelEditRow(drug)}
                                    >
                                      취소
                                    </Button>
                                  </>
                                ) : null}
                                {!isStaging && !isEditing ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={anotherRowEditing}
                                    onClick={() =>
                                      handleStartEditRow(
                                        drug as PrescriptionDrugInfoResponse,
                                      )
                                    }
                                  >
                                    수정
                                  </Button>
                                ) : null}
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={
                                    deleteDrug.isPending || anotherRowEditing
                                  }
                                  onClick={async () => {
                                    const ok = await confirm({
                                      title: '삭제 확인',
                                      description:
                                        '이 의약품을 삭제하시겠습니까?',
                                      confirmLabel: '삭제',
                                      variant: 'destructive',
                                    });
                                    if (!ok) {
                                      return;
                                    }
                                    if (isStaging) {
                                      if (editingRowKey === rowKey) {
                                        setEditingRowKey(null);
                                        setEditDraft(null);
                                      }
                                      removeStagingRow(rowKey);
                                      toast.success(
                                        '검토 목록에서 삭제했습니다.',
                                      );
                                      return;
                                    }
                                    try {
                                      await deleteDrug.mutateAsync(
                                        drug.prescriptionMrId!,
                                      );
                                      if (editingRowKey === rowKey) {
                                        setEditingRowKey(null);
                                        setEditDraft(null);
                                      }
                                      markFormChanged();
                                      toast.success('의약품을 삭제했습니다.');
                                    } catch (e) {
                                      handleApiError(e, toast);
                                    }
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </MenuWriteGate>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                  {displayRows.length > 0 ? (
                    <TableRow className="bg-muted/40 font-medium">
                      <TableCell colSpan={3} className="text-right">
                        합:
                      </TableCell>
                      {/* 합계와 같은 기준(저장 행 + 미저장 OCR 행)으로 목록에 올라온 의약품 수 */}
                      <TableCell>
                        총 의약품 수
                        <br />
                        {displayRows.length.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        총 원외 처방 수량
                        <br />
                        {tableTotals.totalOut.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        총 원내 처방 수량
                        <br />
                        {tableTotals.totalIn.toLocaleString()}
                      </TableCell>
                      <TableCell colSpan={2}>
                        총 처방 금액
                        <br />₩{tableTotals.totalAmount.toLocaleString()}
                      </TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            )}
          </DataCard>
        </div>

        {/* 레거시 PrimaryOutLineBtn / PrimaryBtn — 테이블 하단 액션 */}
        <ToolbarRow gap={8} justify="center" className="pt-2">
          <MenuWriteGate>
            <Button
              variant="outline"
              className="ruda-button-outline"
              onClick={handleOcrRegister}
              disabled={isOcrBusy || !imageDetail.prescriptionImageUrl}
            >
              <ScanLine className="mr-2 h-4 w-4" />
              OCR
            </Button>
            <Button
              variant="outline"
              className="ruda-button-outline"
              onClick={() => void openOcrEditor()}
              disabled={
                isOcrBusy ||
                !imageDetail.prescriptionImageUrl ||
                ocrEditorPreparing
              }
            >
              <ScanLine className="mr-2 h-4 w-4" />
              이미지 편집 OCR
            </Button>
          </MenuWriteGate>
          <Button
            variant="outline"
            className="ruda-button-outline"
            onClick={() => setHighlighterOpen(true)}
            disabled={
              !imageDetail.prescriptionImageUrl || displayRows.length === 0
            }
          >
            <Highlighter className="mr-2 h-4 w-4" />
            하이라이터
          </Button>
          <MenuWriteGate>
            <Button
              className="ruda-button"
              onClick={() => void handleDrugMatching()}
              disabled={isOcrBusy || !hasUnsavedOcrDrugs}
            >
              의약품 매칭
            </Button>
            <Button
              className="ruda-button"
              onClick={() => void handleDrugBatchAddition()}
              disabled={isOcrBusy || !hasUnsavedOcrDrugs || createDrugs.isPending}
            >
              의약품 일괄 추가
            </Button>
          </MenuWriteGate>
        </ToolbarRow>
      </MenuAccessContext.Provider>

      <PrescriptionDrugNameSearchDialog
        open={nameSearchOpen}
        onOpenChange={setNameSearchOpen}
        refYm={effectiveRefYm}
        initialKeyword={nameSearchKeyword}
        draftDrugName={nameSearchDraftName}
        onSelect={(selected) => {
          void applyNameSearchSelection(selected);
        }}
      />

      <Dialog
        open={xlsxUploadDialogOpen}
        onOpenChange={(open) => {
          if (xlsxUploading) return;
          setXlsxUploadDialogOpen(open);
          if (!open) resetXlsxUploadDialog();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>XLSX 의약품 업로드</DialogTitle>
            <DialogDescription>
              XLSX 양식에 맞춰 처방 의약품을 일괄 등록합니다.
            </DialogDescription>
          </DialogHeader>
          <ToolbarRow gap={8}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDownloadXlsxTemplate}
            >
              <Download className="mr-2 h-4 w-4" />
              양식 다운로드
            </Button>
            <FileUploadButton
              inputRef={xlsxInputRef}
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              disabled={xlsxUploading}
              resetOnSelect={false}
              onFilesSelected={(files) => {
                const file = files?.[0];
                if (file) void handleXlsxUpload(file);
              }}
            >
              {xlsxUploading ? '업로드 중...' : '파일 선택'}
            </FileUploadButton>
          </ToolbarRow>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={xlsxUploading}
              onClick={() => setXlsxUploadDialogOpen(false)}
            >
              취소
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {imageDetail ? (
        <WithinImageDuplicateModal
          open={withinDuplicateOpen}
          onClose={() => setWithinDuplicateOpen(false)}
          duplicatedCodes={duplicatedDrugCodes}
          drugs={[...drugs, ...stagingRows]}
          imageDetail={imageDetail}
          prescriptionImageCode={prescriptionImageCode}
          onMerged={async () => {
            // 레거시: refreshPrescriptionDrugs 후 cross-image 검사
            const refreshed = await refetchDrugs();
            await checkCrossImageAndFinalize(refreshed.data ?? []);
          }}
        />
      ) : null}

      <CrossImageDuplicateModal
        open={crossDuplicateOpen}
        onClose={() => setCrossDuplicateOpen(false)}
        items={crossDuplicateItems}
        currentPrescriptionImageCode={prescriptionImageCode}
        localDrugs={drugs}
        refYm={effectiveRefYm}
        onFinalized={navigateBack}
      />

      <Dialog open={ocrEditorOpen} onOpenChange={(open) => !open && closeOcrEditor()}>
        <DialogContent
          showCloseButton
          className="fixed inset-0 left-0 top-0 z-50 flex h-dvh max-h-dvh w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 p-0 sm:max-w-none"
        >
          <DialogHeader className="shrink-0 border-b border-border px-6 py-4 text-left">
            <DialogTitle>이미지 편집 OCR</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
            {ocrEditorPreparing ? (
              <LoadingSpinner tip="편집 영역을 준비하는 중..." />
            ) : (
              <OcrCropWarpUploader
                value={editedOcrImages}
                onChange={setEditedOcrImages}
                maxCount={1}
                sourceImageUrl={imageDetail.prescriptionImageUrl}
                sourceInitialPoints={ocrSourceCorners ?? undefined}
                sourceImageName={prescriptionImageCode}
                allowFileSelect={false}
                showSourceButton={false}
                preprocessMode="warp-crop"
                maxSourceEdge={Number.POSITIVE_INFINITY}
                maxWarpWidth={Number.POSITIVE_INFINITY}
                maxWarpHeight={Number.POSITIVE_INFINITY}
                onSelectionChange={setOcrLiveSelection}
              />
            )}
          </div>
          <DialogFooter className="shrink-0 border-t border-border px-6 py-4 sm:justify-end">
            <Button variant="outline" onClick={closeOcrEditor}>
              닫기
            </Button>
            <Button
              onClick={() => void runEditedOcrComparison()}
              disabled={
                editedOcrComparing ||
                ocrEditorPreparing ||
                !(
                  (editedOcrImages[0]?.ocrMeta?.corners?.length === 4 &&
                    editedOcrImages[0]?.ocrMeta?.bbox) ||
                  (ocrLiveSelection?.corners?.length === 4 &&
                    ocrLiveSelection?.bbox)
                )
              }
            >
              OCR 요청 실행
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditedOcrComparisonModal
        open={comparisonOpen}
        onOpenChange={setComparisonOpen}
        defaultRows={comparisonResults.default}
        geminiRows={comparisonResults.gemini}
        onApply={(source) => void applyEditedOcrResult(source)}
        applying={comparisonApplying}
        previewUrl={editedOcrRequestPreviewUrl}
      />

      <PrescriptionExclusionDialog
        open={exclusionDialogOpen}
        onOpenChange={setExclusionDialogOpen}
        defaultReason={imageDetail.exclusionReason}
        submitting={excludeImage.isPending}
        onSubmit={(reason) => void handleExclude(reason)}
      />

      <PrescriptionHighlighterDialog
        open={highlighterOpen}
        onOpenChange={setHighlighterOpen}
        imageUrl={imageDetail.prescriptionImageUrl}
        imageName={`${imageDetail.clinicName || 'clinic'}_${prescriptionImageCode}`}
        drugs={displayRows}
        yearMonth={prescriptionYearMonth || imageDetail.prescriptionYearMonth}
        prescriptionImageCode={prescriptionImageCode}
      />

      <AlertDialog open={quitDialogOpen} onOpenChange={setQuitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>확인</AlertDialogTitle>
            <AlertDialogDescription>
              변경된 내용이 삭제됩니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction onClick={navigateBack}>
              확인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
